import React from 'react'

import { Section, Card, TrustLine } from '../../../../components/ui'
import { washCountLabel } from '../../../../lib/social-proof'
import { image } from '../../../../lib/image'
import { largeCarLine } from '../../../../lib/price-lines'
import { readWashes } from '../../../../lib/page-prices'
import { useSelectors } from '../../../../store'
import { HERO_FRAMES, FRAME_W, FRAME_H, frameCaption } from './frames'

import './hero.css'

// SLOT 7.1 - THE FOLD. Design direction §2.1 (1440) and §2.2 (390), plus the
// OWNER DIRECTION and CORRECTION of 4 Oct, and REBUILT 31 Oct 2026 to Fable's
// ruling (docs/DECISIONS.md).
//
// This is the whole site: the median visitor sees the top 36% of the page and
// nothing else. 35 of 37 booking sessions start on the home page, so this
// screen is where 20 orders a month are won or lost, and 88.5% of the people
// who open the booking form never pay. Every line below is here because it
// answers a question that stops a booking, or it has no business on the fold.
//
// ===========================================================================
// THE FIRST PHONE SCREEN IS FOUR THINGS (2 Nov 2026)
// ===========================================================================
//
// Robin, verbatim: "put barry in the hero instead, high up and then just the
// 3 wash tiles". So:
//
//   1. H1          "Mobile valeting on demand, anywhere in London."
//   2. subhead     "Car washing and valeting wherever your car is parked.
//                  Fixed prices from £69, booked in two minutes."
//   3. BARRY       the cut-out and his caption, high up, as the hero's own
//                  photograph. He was 1,648px down the page until today.
//   4. the TILES   the three washes, each ONE link to that wash's own page.
//
// Nothing else is on it. The trust line, the surcharge footnote with its
// "Compare all washes", the proof heading and the filmstrip are all BELOW,
// in the DOM, with no `order` or grid-row tricks, so the paint order and the
// reading order are the same at every width.
//
// WHAT THIS REVERSES, and both reversals are recorded as dated supersedes in
// docs/DECISIONS.md:
//
// A. THE FOLD'S PHOTOGRAPH IS BARRY, NOT AN OPERATOR AT WORK. The 31 Oct
//    build spent HERO_SHOT (gallery-image-01, the one frame with an operator,
//    the van and the work in it) on the fold and pulled it out of the strip so
//    the same picture did not appear twice. It goes back to the strip, where
//    it led until 31 Oct, and nothing is orphaned.
//    THE RULE THE 31 OCT BUILD ITSELF BROKE was "no image above the fold on
//    the phone: the H1 is the LCP element", which is what repaired a 15.5s
//    mobile LCP. It has not been in force since 31 Oct, so this change does
//    not break it; it moves the budget the other way. Barry's cut-out paints
//    at 108px tall and about 89px wide on a phone, roughly 9.6k px² against
//    the band's 350 x 109 (38k px²) and the H1's 350 x 126 (44k px²), so the
//    H1 is the largest painted element on the first screen again and takes
//    the LCP back. The file is lighter too: 72 KB against the band's 106 KB,
//    and the band's frame is lazy again down in the strip.
//
// B. THE ONE BUTTON IS OFF THE FOLD. The 31 Oct build made the first screen
//    one primary action, "Book a Mini Valet, £69", straight into Book & pay
//    with that wash ticked, plus a "Compare all washes" link under it. Both
//    are gone from the first screen: the three tiles ARE the action, and
//    Robin's standing direction since 31 Oct is card to page to form, which
//    routes a visitor to the dedicated service page for the wash they want
//    rather than into the form. `entry_source: 'hero_primary'` therefore
//    stops firing, by design, exactly as `hero_wash_card` did (ANALYTICS.md);
//    one after today means a button has come back to the fold.
//    THE FORM IS NOT ORPHANED, and this was checked rather than assumed: the
//    HEADER bar carries "Book now" to /booking/book on every page at every
//    width (components/header, app/views/gwmc/_header.html.haml,
//    BookingLinks.general), one tap from this screen; and every wash page has
//    its own Book above its own fold on that wash plus a phone sticky bar
//    (WashPage#booking_path, app/views/gwmc/_wash_page.html.haml).
//    SHORTEST PATH TO A PAID BOOKING, IN TAPS: TWO, which is what it was
//    before. Walked in a browser at 390: the header's "Book now" opens the
//    form with Mini Valet ticked and priced, the first bookable day chosen and
//    Anytime chosen (the dumb form's defaults), and "Book my wash, £69" at the
//    foot of that one screen is the second tap. The 31 Oct fold was also two.
//    So removing the button costs no taps; what it costs is that the target
//    moved from a 56px full-width button in the middle of the first screen to
//    the header bar's compact one, and that a link naming no wash opens the
//    picker, which is the decision the 31 Oct amendment removed. By the tiles
//    it is three (tile, the wash page's own Book, pay), which is the
//    card-to-page-to-form route and the point of it.
//
// MEASURED, not estimated, in a browser at all three widths (DESIGN.md §5);
// the per-element numbers are in ./hero.css. AT 390 the H1 runs 88-172, the
// subhead 184-262, BARRY 278-386 and the tiles 402-556, 564-718 and 726-880,
// so THE 660px LINE FALLS 96px INTO TILE 2 AND TILE 3 IS 66px BELOW IT. The
// third tile does not fit and nothing was quietly dropped to pretend it does:
// tile 1 came up 229px (the button, the compare link, the band and the trust
// line held 353px between the subhead and the first card; Barry holds 124px)
// and three tiles are 478px, which is 220px more than the screen has. React
// paints the same heights, 0px drift on every element at every width. LCP is
// the H1, 384ms at 390.
//
// WHY THE REST IS AS IT IS, because each of these replaced something that was
// working to a measurable degree and the reasoning has to survive the next
// rebuild:
//
// C. THE H1 NAMES THE BUSINESS, NOT THE METHOD (2 Nov 2026). Robin: "should
//    be mobile valeting on demand or something like this", and "dont go so
//    heavy on hand wash tbh.. we're not doing two bucket method". It read
//    "We come to you and hand wash your car where it's parked." from 31 Oct,
//    which made hand washing the drumbeat and invited a comparison with
//    detailing ritual we would lose; before that it was the search phrase
//    read back to the visitor.
//    BOTH VOCABULARIES, ACROSS THE SITE AND DELIBERATELY (Robin, same day:
//    "we need to hedge that vocab tbh and the site needs to be littered
//    with both car wash and valeting vocab"). The H1 carries the positioning
//    word and the subhead carries the other, naturally; <title> and the meta
//    description keep "mobile car wash & valeting" where Google reads them
//    (MetaTags::Helper). "Car wash" is 179,670 London searches a month and
//    62.9% of our Search Console clicks; valeting is the biggest come-to-me
//    family and matches the price band. No sentence exists only to hold a
//    keyword. AREA pages keep their own pattern: the place name leads there.
// D. THE TRUST LINE CARRIES THE WASH COUNT, which used to be the eyebrow.
//    The fold has no eyebrow at all, so the strongest number the business
//    owns sits in the line that answers "who is coming and what do I get".
//    It is below the tiles now rather than above them, because Robin's
//    sentence puts the tiles straight after Barry and nothing between them.
//    Its first item is still MetaTags::Helper.wash_count_label / the live
//    /api/v2/app_config count and is never typed.
// E. THE GOOGLE SCORE CANNOT APPEAR HERE. It rode on SHOW_SOCIAL_PROOF, so
//    turning the wash count back on would have dragged "★ 4.1 on Google"
//    onto the fold. It is SHOW_GOOGLE_RATING's now (AppSecrets.google_rating
//    is nil while that is off), and this file reads no rating at all.
//
// WHAT IS UNCHANGED: the three washes and their order (Exterior Wash & Wax,
// Mini Valet, Mini Valet PLUS; Robin, 25 Oct 2026), the "Large car +£20"
// line on each card, the one action per card and its "See more and book"
// label, the Congestion Zone sentence, the trust line's three facts, and
// the scroll-snap filmstrip with its real captions (which has its 01 frame
// back).
//
// THE SHAPE, and why it is this shape.
//
// Robin, 4 Oct: "I don't like the hero image tbh.. we need images of cars
// being washed, maybe carousel or tile view, or fade in and out on hero? but
// dudes washing cars." So: action, people, work being done. The fold's one
// photograph and the strip below it are the curated washes in ./frames.js,
// each captioned with the real service and the real London outward postcode
// the server already publishes for it. The caption is the whole argument.
// "Mini Valet, SW18" is a claim stock photography cannot make.
//
// Four constraints decide the layout, all from DESIGN.md §3:
//
//   1. No carousel library. The home chunk has a 250 KB budget and react-slick
//      is ~90 KB of it. The strip below is native CSS scroll-snap: a flex row
//      in an overflow-x container, `scroll-snap-type: x mandatory` on the
//      container and `scroll-snap-align: start` on each frame. Swipe on a
//      phone, trackpad or arrow keys on a laptop, no JavaScript at all.
//   2. No text over a photograph and no gradient over one. So the caption sits
//      under its own frame, and the H1 is beside or above the photograph,
//      never on it. That rules out the usual "one big photo, headline on top"
//      hero. It is not a limitation to design around; it is the design.
//   3. No background-image hero. Real <img> elements, each with width and
//      height attributes, so the boxes are reserved before the bytes land.
//   4. All the source files are portrait 640 x 853 and the strip keeps them
//      portrait. The fold's photograph is Barry's own 810 x 987 alpha cut-out,
//      drawn at its natural ratio and never re-cut: the band-with-object-fit
//      crop the 31 Oct build needed left with the frame it cropped.
//
// WHAT MUST NOT REGRESS: one H1 per page, server-rendered from the
// gwmc:header / gwmc:subheader meta tags so the area pages keep their own
// headline (the September SEO overhaul depends on it and breaking it is
// silent); every price, including the button's "from", read from the
// gwmc:prices meta the DB fills and never baked into the bundle; and
// app/views/gwmc/_hero.html.haml, which paints this same markup, with the
// same classes and the same height, before the bundle has run.

// The H1, the subhead and the prices all come from the server so that one
// code path serves the home page and every area page.
const meta = (name, fallback = null) =>
  (typeof document === 'undefined'
    ? null
    : document.querySelector(`meta[name='${name}']`)?.getAttribute('content')) || fallback

// Prices are read once, synchronously, from the gwmc:prices meta the layout
// writes on every render. There are no baked prices in the bundle (they drift
// stale: this file used to be able to say "Mini Valet £49") and the fold does
// not fetch /api/v2/services, because an XHR in front of the LCP is exactly
// the cost this rebuild exists to remove.
const readPrices = () => {
  try {
    return JSON.parse(meta('gwmc:prices', '{}'))
  } catch (e) {
    return {}
  }
}

// THE HOME SUBHEAD, as the server writes it (MIRROR of
// MetaTags::Helper#h2). This is only the bundle's fallback for a page served
// without gwmc:subheader; the server's sentence is what a reader sees, and
// the two have to agree. No price is written here: the amount comes off
// gwmc:prices like every other figure on the fold.
const foldSubhead = from =>
  `Car washing and valeting wherever your car is parked. Fixed prices${
    from ? ` from ${from}` : ''
  }, booked in two minutes.`

// THERE IS NO readRating() ANY MORE, and that is the point. The eyebrow used
// to append "★ 4.1 on Google" whenever the gwmc:reviews meta tag carried a
// rating, which it did whenever SHOW_SOCIAL_PROOF was on. The score has its
// own gate now (SHOW_GOOGLE_RATING; AppSecrets.google_rating is nil while it
// is off, so the tag carries no rating at all), and the fold does not read
// one even when it is there: Barry's rule is no score on the site until the
// public one is 4.5 or better. The reviews section (sections/reviews) is the
// one place that may draw a score, and it reads the same gated tag.

// The server's gwmc:header, split one line per sentence. The home page sends
// one sentence ("Mobile valeting on demand, anywhere in London.",
// MetaTags::Helper::HOME_H1) and so renders as one block; the area pages
// still send two ("Mobile car wash in Chelsea. Professional valeters at your
// door.")
// and still break between them. The split stays for them.
//
// The fallback is the area-page pattern on purpose: a page served without the
// meta tag is not the home page, and the home page always has one.
const HeroH1 = () => {
  // Mirror of MetaTags::Helper::HOME_H1, where the reasoning lives. The
  // 2 Nov register pass deliberately left this line alone: Robin wrote it
  // himself that morning and has not ruled on changing it. The alternative,
  // if he takes it, is a one-line swap here and there:
  //   'Professional mobile valeting, anywhere in London.'
  const text = meta('gwmc:header', 'Mobile valeting on demand, anywhere in London.')
  const parts = text.split(/(?<=\.)\s+/)
  return (
    <h1 className="hero-h1">
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && <br />}
          {part}
        </React.Fragment>
      ))}
    </h1>
  )
}

// Three washes, in the site's order (lib/wash-order.js). Name and price top,
// one line of description, then two actions whose labels say where they go
// (Robin, 25 Oct 2026: "if it says book now they might expect the booking
// form, but if they want more info they want the dedicated page to build
// commitment, and then the form"): "See what's included" and the name open
// the wash's own page (/services/<slug>, a document load), "Book now" opens
// the form on that wash (`href` for a crawler and a middle click,
// `enterBooking` to keep the SPA from reloading the bundle).
//
// THE WORDS ARE A VALETER'S, NOT A PRODUCT MANAGER'S, and that is the whole
// point of them. Robin, 4 Oct: "folks would trust a site where they feel this
// team can really execute, industry standard best guys in the space.. not some
// tech bros". He is right, and "premium" is banned by name in DESIGN.md §3
// precisely because it is what you write when you have no detail.
// "Professional" sat in that bullet until Robin ruled it allowed on 5 Oct
// (§3.1); these cards still carry no adjective, because what sells them is the
// noun. Trade credibility is specificity nobody outside the trade bothers
// with: "tyres dressed", "boot and bonnet gutters", "HD Carnauba wax coating",
// "upholstery shampooed". Nobody writing a dog-walking app writes "gutters".
//
// EVERY NOUN BELOW IS A ROW IN THE SERVICES MATRIX. The source is
// pages/services/compare/washes.js FEATURE_GROUPS, which is the catalogue's
// only written-down form, and these three lines are a summary of the ticks in
// that wash's column rather than a second opinion about what it includes. The
// matrix is the evidence: `in: [...]` indexes the washes and the ticks are
// what the booking actually sells. An invented method would be worse than
// bland copy, so nothing here is invented.
//   Mini Valet column: exterior hand wash, wheels cleaned and tyres dressed,
//     seats and carpet and mats and boot vacuumed, door shuts and door cards
//     and centre console and dash wiped.
//   Mini Valet PLUS column: all four of those, plus basic body wax, boot and
//     bonnet gutters cleaned, screenwash and oil and fluid check, interior
//     windows cleaned. It is a strict superset of the Mini Valet, which is why
//     its line is allowed to say "The Mini Valet, plus ...".
//   Full Valet column: NOT a superset of the PLUS (it drops the basic body wax
//     for the HD Carnauba coating), so its line does not nest. What it adds is
//     the engine bay, the HD Carnauba wax coating or sealant, the upholstery
//     and carpets shampooed, the spare wheel area.
//
// NOT "Hand wash, ..." ON EVERY CARD (2 Nov 2026). All three lines opened on
// the same two words, which did none of the work of telling the cards apart
// and made the fold say "Hand wash" four times in one scroll. Robin: "also
// dont go so heavy on hand wash tbh.. we're not doing two bucket method". The
// nouns are unchanged and still come off the matrix; only the shared opener
// is gone, and the washing is still said to be done by hand further down the
// page (How it works, the process block, the valeter row), where it informs.
//
// What the "Hand wash, ..." lines had replaced read "Outside only." /
// "Outside, plus a vacuum and a wipe inside." / "Outside and inside, line by
// line." Those are the comparative
// descriptors from /services and they are right THERE, in a six-column table
// whose job is to tell six washes apart. The fold has a different job: it has
// to make a stranger believe we can do the work. "Outside and inside, with
// wax" does not; "body wax, interior glass, a fluid check and the boot and
// bonnet gutters" does, and it says it without one adjective.
//
// TWO LENGTHS PER WASH, and it is not laziness. At 1440 the card is 368px wide
// and shows `description` in full over two lines. At 390 the card is 84px tall
// and the description gets ONE line: the fold used to put the long string
// there and truncate it mid-word with an ellipsis, which is worst of all on a
// card whose description is the only thing telling it from the card above.
// `short` is the same list inside the width the phone has. Neither string says
// anything the other denies; `short` says less.
//
// The direction also offered a duration on each card ("About 45 minutes") on
// the condition that Barry would stand behind it. Nobody has asked him, so the
// durations are not here: its own instruction for that case is to keep the
// first sentence and drop the second. Add them back when he has signed them
// off, not before: a time we miss is a refund conversation.
const WASHES = [
  {
    slug: 'exterior_wash_and_wax',
    name: 'Exterior Wash & Wax',
    // The Exterior Wash & Wax column: exterior hand wash, wheels cleaned and
    // tyres dressed, basic body wax.
    short: 'Washed and waxed, wheels and tyres.',
    description: 'Washed and waxed outside, wheels cleaned and tyres dressed.'
  },
  {
    slug: 'mini_valet',
    name: 'Mini Valet',
    short: 'Washed outside, vacuumed inside.',
    description: 'Washed outside, wheels cleaned and tyres dressed, then vacuumed and wiped inside.'
  },
  {
    slug: 'mini_valet_plus',
    name: 'Mini Valet PLUS',
    // True by a distance: about two thirds of all orders. DESIGN.md §3 bans
    // "Popular" ribbons and records this one label as Robin's exception.
    label: 'Most booked',
    short: 'The Mini Valet, with wax and gutters.',
    description: 'The Mini Valet, plus body wax, interior glass, a fluid check and the boot and bonnet gutters.'
  }
]

// A missing price is a skeleton, never a guess.
const Price = ({ value }) =>
  value ? (
    <span className="hero-wash-price">{value}</span>
  ) : (
    <span className="hero-wash-price hero-wash-price--pending" aria-hidden="true" />
  )

// large: the large-car surcharge ("£20"), for the "Large car +£20" line
// under the description (19 Oct 2026, docs/DECISIONS.md). `page`: the wash's
// own page, off gwmc:catalogue (/services when it has none).
// CARD TO PAGE TO FORM (Robin, 31 Oct 2026). The whole card is ONE link to
// that wash's own page, and it has no second action.
//
// Robin: "how they choose the wash I think they first need to go to a
// detailed page and then from there to booking form", "lets try in that
// direction more", "I know we need more traffic we need card to page to form
// pls", "until lower down the page, lets try", and on the shipping of it,
// "no A B, that as default". His 25 Oct row already said the same thing: "if
// it says book now they might expect the booking form, but if they want more
// info they want the dedicated page to build commitment, and then the form".
//
// WHAT IT COSTS, because this is the risky half of the release. 90 days,
// customers only: 11 people read a wash page before entering the form and 2
// paid (18.2%), against 170 who did not with 12 paid (7.1%). Directionally
// in favour and tiny and intent-confounded. Meanwhile `hero_wash_card` is 71
// people with 7 paid (9.9%) on real volume, and "See what's included" was
// tapped 16 times in 90 days. So a barely-used path is being made the only
// one here, at some risk to a proven one. That is why it is its own commit
// and why the DECISIONS row carries a revert condition.
//
// THE LABEL IS "See more and book", not "See what's included" (Robin: "yea
// see whats included or see more detail, or see more and book"). Now that
// the tap is the card's only action it has to say two things at once: there
// is detail behind it AND booking happens there. "See what's included" reads
// as a dead end to somebody who has already decided, and that is exactly the
// visitor the direct Book used to serve.
//
// THE DIRECT ROUTE IS NOT GONE, it has moved down the page: sections/
// whats-included keeps a per-wash "Book now" straight into the form on that
// wash, with `entry_source: 'included_section_button'` untouched, so the two
// routes stay comparable. `hero_wash_card` keeps its name and will simply
// stop firing from here, which is the measurement.
//
// It is a <span> styled as a button and not a <button>, because the card is
// already the link and a control inside a link is neither.
const WashCard = ({ wash, price, large, page }) => (
  <Card href={page} className="hero-wash" data-wash-card={wash.slug}>
    <span className="hero-wash-name">
      {wash.name}
      {wash.label && <span className="gwmc-tag hero-wash-tag">{wash.label}</span>}
    </span>
    <Price value={price} />
    <span className="hero-wash-desc">
      <span className="hero-wash-desc-short">{wash.short}</span>
      <span className="hero-wash-desc-long">{wash.description}</span>
    </span>
    {large && <span className="hero-wash-large">{largeCarLine(large)}</span>}
    <span className="hero-wash-go">
      <span className="gwmc-btn gwmc-btn--primary hero-wash-more">
        See more and book<span className="gwmc-sr-only"> the {wash.name}</span>
      </span>
    </span>
  </Card>
)

// The Congestion Zone surcharge, by name, on the first screen. A price that
// grows at checkout is how a form loses 88.5% of the people who open it. The
// large car's moved onto each card on 19 Oct 2026 ("Large car +£20"). The
// number comes from gwmc:prices; if the server has not sent it, the sentence
// does not appear, because a surcharge we cannot name is worse than silence.
// "Compare all washes" goes to the table on /services (#compare; Robin,
// 25 Oct 2026: "the compare washes should be better linked throughout").
const Surcharges = ({ prices }) => {
  const zone = prices.congestion_zone
  const compare = (
    <a className="gwmc-link" href="/services#compare">
      Compare all washes
    </a>
  )
  if (!zone) return <p className="hero-more">{compare}</p>

  return (
    <p className="hero-more">
      <span className="hero-more-adds">{`Congestion Zone adds ${zone}.`}</span>
      <span className="hero-more-link">
        {' · '}
        {compare}
      </span>
    </p>
  )
}

// BARRY, THE FOLD'S ONE PHOTOGRAPH AND ITS ONLY IMAGE (Robin, 2 Nov 2026:
// "put barry in the hero instead, high up and then just the 3 wash tiles").
// He was at the foot of this section, 1,648px down the page, where only the
// 36% of visitors who scroll that far could see him; he is on the screen 100%
// of them see now.
//
// A real alpha cut-out that fades out above the crop through his hands, so he
// stands on --paper with no disc, no band and no halo behind him, which is
// why he needs no frame and no object-fit crop.
//
// EAGER and fetchPriority high, which is the opposite of every other image in
// this file and is deliberate: he is ON the first screen at every width, the
// layout preloads him to match, and a lazy image here is a hole under the
// promise. The H1 is still above him in the DOM and still painted first, and
// at 108px tall on a phone he is a far smaller LCP candidate than the band he
// replaces, so the H1 is the largest thing on the first screen again.
//
// THE CAPTION IS UNCHANGED, word for word, from the block this reuses. It
// claims no method, names no kit and says "valeter", never "washer".
const HeroBarry = () => (
  <figure className="hero-barry">
    <img
      className="hero-barry-img"
      src={image('./bossman_barry.webp')}
      alt="Barry Cohen, head of operations at GoWashMyCar"
      width="810"
      height="987"
      loading="eager"
      decoding="async"
      fetchPriority="high"
    />
    <figcaption className="hero-barry-caption">
      <span className="hero-barry-name">Barry Cohen</span>
      <span className="hero-barry-role">Head of operations</span>
      <span className="hero-barry-line">
        Trains and vets every valeter. Valeting in London since 1999.
      </span>
    </figcaption>
  </figure>
)

// The filmstrip. A scroll container with no JavaScript in it: `tabindex=0`
// and a label make it a keyboard-reachable scrollable region, which is what
// replaces the arrow buttons a carousel library would have brought.
//
// EVERY frame is lazy, and now unarguably so: the strip is below the first
// screen at every width, so nothing in it competes with Barry or the H1 for
// the LCP. One attribute, both renderers, no width sniffing. The 01 frame
// (an operator, the van and the work, SW18) leads it again, as it did until
// 31 Oct: the fold no longer shows it, so it is not on the page twice.
const Filmstrip = () => (
  <div
    className="hero-strip"
    tabIndex={0}
    role="group"
    aria-labelledby="hero-proof-heading">
    {HERO_FRAMES.map(frame => (
      <figure className="hero-frame" key={frame.file}>
        <img
          className="hero-frame-img"
          src={image(`./${frame.file}`)}
          alt={frame.alt}
          width={FRAME_W}
          height={FRAME_H}
          loading="lazy"
          decoding="async"
        />
        <figcaption className="hero-frame-caption">{frameCaption(frame)}</figcaption>
      </figure>
    ))}
  </div>
)

export const HeroFold = () => {
  // Live wash count from /api/v2/app_config, for the trust line's first item.
  // The fallback is 26,000+, not 10,000+: there have been 26,088 washes since
  // 2016 and the old fallback undersold the business by sixteen thousand
  // cars. The server paints the same number from the same cache key, so the
  // line does not change width when the bundle mounts over it. Null while
  // SHOW_SOCIAL_PROOF is off (lib/social-proof.js), and then TrustLine drops
  // the item and leaves no stray separator behind.
  const { config } = useSelectors()
  const washLabel = washCountLabel(config)

  const prices = readPrices()
  const pages = Object.fromEntries(readWashes().filter(wash => wash.path).map(wash => [wash.slug, wash.path]))

  // THERE IS NO ctaLabel ANY MORE, and that is the change (Robin, 2 Nov
  // 2026). The fold's one button, "Book a Mini Valet, £69", read the
  // catalogue's default wash and that wash's own price out of gwmc:prices,
  // and it went straight into Book & pay. It is gone from the first screen:
  // the three tiles are the action and each one opens that wash's own page.
  // `defaultWash`, `bookingPath` and `enterBooking` are not imported here
  // either, so nothing in this file can quietly grow a second route into the
  // form. The header's "Book now" is the fold's one tap to it.

  return (
    <Section className="hero">
      <div className="hero-grid">
        <div className="hero-copy">
          <HeroH1 />
          <p className="hero-subhead">
            {/* The server sends the whole sentence on gwmc:subheader so the
                area pages bring their own. The fallback is the home page's,
                MIRROR of MetaTags::Helper#h2: "car washing" against the H1's
                "valeting", then the come-to-you promise, the fixed price and
                the speed. The amount is prices.from off gwmc:prices and is
                never baked in; with no price the sentence still reads. The
                fold states no charge time at all now, on either setting of
                CHARGE_ON_COMPLETION, so there is no pay clause left to keep
                in step with the switch (lib/payment-copy.js). */}
            {meta('gwmc:subheader', foldSubhead(prices.from))}
          </p>
        </div>

        {/* BARRY, HIGH UP: the third thing on the first screen and its only
            image. */}
        <HeroBarry />

        {/* AND THEN JUST THE THREE WASH TILES, the fold's only action. */}
        <div className="hero-washes">
          {WASHES.map(wash => (
            <WashCard key={wash.slug} wash={wash} price={prices[wash.slug]} large={prices.large_car} page={pages[wash.slug] || '/services'} />
          ))}
        </div>

        {/* ---- Below the first screen ------------------------------------ */}
        <Surcharges prices={prices} />

        {/* THE PROOF BAND. The same three facts in the same order; the count
            is set as a figure and its words, at two sizes, because a number
            nobody else can print was reading as a footnote (Robin, 2 Nov
            2026: "amateur jobs"). `{' '}` is there so the two spans read as
            one sentence to a screen reader and so this markup matches the
            Haml's, which has the newline between them; the space the eye
            sees is the band's own gap. MIRROR of
            app/views/gwmc/_hero.html.haml and HeroHelper#hero_trust_items. */}
        <TrustLine
          className="hero-trust"
          items={[
            washLabel && (
              <span className="hero-trust-count">
                <span className="hero-trust-figure">{washLabel}</span>{' '}
                <span className="hero-trust-label">cars washed since 2016</span>
              </span>
            ),
            'Vetted and insured',
            'Photos of the finished car on your phone'
          ]}
        />

        <h2 className="gwmc-h2 hero-proof-heading" id="hero-proof-heading">
          Our valeters at work, all over London
        </h2>
        <Filmstrip />
      </div>
    </Section>
  )
}
